import {FunctionComponent, MouseEventHandler, ReactNode, useCallback, useMemo} from "react";
import {ErrorOutline} from "@mui/icons-material";
import type {ToastContentProps} from "react-toastify";
import ToastContent from "../../index";
import {Box, Button} from "@mui/material";
import BizkeytechReduxStore, {BizkeytechReduxActions} from "../../../../redux";
import {ApiRequestDs, ApiResponseDs, EnvService} from "../../../../../core";

export interface IApiResponseErrorToastContent {
    message: string,
    title?: string,
    requestIntent?: string,
    submitTicket?: boolean,
    requestModel: ApiRequestDs<unknown>,
    responseModel?: ApiResponseDs<unknown> | null,
}

const ApiResponseErrorToastContent: FunctionComponent<ToastContentProps<IApiResponseErrorToastContent>> = (props) => {
    const {closeToast} = props;

    /**
     * Opens the ticket submission dialog.
     */
    const submitTicket = useCallback<MouseEventHandler<HTMLElement>>((e) => {
        e.preventDefault();
        e.stopPropagation();
        BizkeytechReduxStore.dispatch(BizkeytechReduxActions.dialogs.submitTicket({
            open: true,
            data: {
                requestModel: props.data.requestModel,
                responseModel: props.data.responseModel,
            },
            onClose: (reason) => {
                if (reason !== 'successful') {
                    return;
                }
                closeToast();
            }
        }));
    }, [closeToast, props.data.responseModel, props.data.requestModel])

    const message = useMemo<string>(() => {
        let result = props.data.message ?? "";
        if (props.data.requestIntent) {
            result = `Intent: ${props.data.requestIntent}
                       <br/>
                       <span style="margin-block: 5px;display: table;"></span>
                      ${result}`;
        }
        return result;
    }, [props.data.message, props.data.requestIntent])

    const children = useMemo<ReactNode>(() => {
        const errors = props.data.responseModel?.errors;
        const useChildren = !!errors && typeof errors === 'object';
        // Handling the case where the response has errors object.
        if (!useChildren) {
            return null;
        }
        return (
            <>
                <Box component={'ul'} sx={{marginBlock: 0}}>
                    {
                        Object.keys(errors).map((key) => {
                            return (
                                <Box key={key} component={'li'} sx={{marginLeft: '-38px'}}>
                                    <Box
                                        component={'span'}
                                        sx={(theme) => ({...theme.typography.body2})}
                                    >
                                        {key}
                                    </Box>
                                    <Box component={'ul'}>
                                        {
                                            errors[key].map((err) => {
                                                return (
                                                    <Box
                                                        key={err}
                                                        component={'li'}
                                                        sx={(theme) => ({...theme.typography.caption, marginLeft: '-28px',})}
                                                    >
                                                        {err}
                                                    </Box>
                                                )
                                            })
                                        }
                                    </Box>
                                </Box>
                            )
                        })
                    }
                </Box>
            </>
        );
    }, [props.data.responseModel?.errors])

    return (
        <>
            <ToastContent
                title={props.data.title}
                message={message}
                icon={<ErrorOutline color={'error'}/>}
                className={typeof props.toastProps.className === 'string' ? props.toastProps.className : ''}
                toastProps={props.toastProps}
                actions={
                    <>
                        {
                            EnvService.showTicketSubmissionFeature && props.data.submitTicket &&
                            <Button variant={'outlined'} size={'small'} onClick={submitTicket}>
                                Submit Ticket
                            </Button>
                        }
                    </>
                }
                useChildren={!!children}
                children={children}
            />
        </>
    )
}

export default ApiResponseErrorToastContent;
